import { createContext, useContext, useEffect, type ReactNode } from 'react';
import {
  type Theme,
  type BrandVariants,
  createLightTheme,
  createDarkTheme,
} from '@fluentui/react-components';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Maja Brand Colors (Microsoft 365 style)
const majaBrand: BrandVariants = {
  10: '#020305',
  20: '#111723',
  30: '#16263D',
  40: '#193253',
  50: '#1B3F6A',
  60: '#1B4C82',
  70: '#18599B',
  80: '#1267B4',
  90: '#3174C2',
  100: '#4F82C8',
  110: '#6790CE',
  120: '#7E9ED5',
  130: '#94ACDB',
  140: '#AAB9E1',
  150: '#BFC7E8',
  160: '#D4D5EE',
};

const majaLightTheme = createLightTheme(majaBrand);
const majaDarkTheme = createDarkTheme(majaBrand);

// Predefined color palettes
export const accentColors = [
  { name: 'Blue', value: '#0078d4' },
  { name: 'Green', value: '#107c10' },
  { name: 'Purple', value: '#5c2d91' },
  { name: 'Red', value: '#e81123' },
  { name: 'Teal', value: '#008272' },
  { name: 'Orange', value: '#ff8c00' },
  { name: 'Pink', value: '#e3008c' },
  { name: 'Gray', value: '#69797e' },
  { name: 'Navy', value: '#002050' },
  { name: 'Cyan', value: '#0078d7' },
];

export const navigationColorsLight = [
  { name: 'Light Gray', value: '#f3f2f1' },
  { name: 'White', value: '#ffffff' },
  { name: 'Light Blue', value: '#e8f1fc' },
  { name: 'Light Green', value: '#e8f5e9' },
  { name: 'Cream', value: '#faf9f8' },
];

export const navigationColorsDark = [
  { name: 'Dark Gray', value: '#323130' },
  { name: 'Charcoal', value: '#201f1e' },
  { name: 'Navy', value: '#0d1a2d' },
  { name: 'Dark Green', value: '#0d2818' },
];

export type ThemeMode = 'light' | 'dark' | 'system';
export type ColorScheme = 'light' | 'dark';

interface ThemeState {
  mode: ThemeMode;
  accentColor: string;
  headerColorLight: string;
  headerColorDark: string;
  navigationColorLight: string;
  navigationColorDark: string;
  navigationTextColorLight: string;
  navigationTextColorDark: string;
  logoUrl: string | null;
  setMode: (mode: ThemeMode) => void;
  setAccentColor: (color: string) => void;
  setHeaderColor: (scheme: ColorScheme, color: string) => void;
  setNavigationColor: (scheme: ColorScheme, color: string) => void;
  setNavigationTextColor: (scheme: ColorScheme, color: string) => void;
  setLogoUrl: (url: string | null) => void;
  reset: () => void;
}

const defaultState = {
  mode: 'light' as ThemeMode,
  accentColor: '#0078d4',
  headerColorLight: '#0078d4', // Office 365 blue header
  headerColorDark: '#1b1a19',
  navigationColorLight: '#f3f2f1', // Light gray sidebar like Office 365
  navigationColorDark: '#201f1e',
  navigationTextColorLight: '#323130',
  navigationTextColorDark: '#f3f2f1',
  logoUrl: '/images/logo.png',
};

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      ...defaultState,
      setMode: (mode) => set({ mode }),
      setAccentColor: (accentColor) => set({ accentColor }),
      setHeaderColor: (scheme, color) =>
        set(scheme === 'dark' ? { headerColorDark: color } : { headerColorLight: color }),
      setNavigationColor: (scheme, color) =>
        set(scheme === 'dark' ? { navigationColorDark: color } : { navigationColorLight: color }),
      setNavigationTextColor: (scheme, color) =>
        set(
          scheme === 'dark'
            ? { navigationTextColorDark: color }
            : { navigationTextColorLight: color }
        ),
      setLogoUrl: (logoUrl) => set({ logoUrl }),
      reset: () => set(defaultState),
    }),
    {
      name: 'maja-theme',
      version: 2,
      migrate: (persisted, version) => {
        if (!persisted || typeof persisted !== 'object') return persisted as ThemeState;
        if (version < 2) {
          // v1 had single-color slots; migrate them to the light scheme and seed dark defaults.
          const legacy = persisted as Partial<{
            headerColor: string;
            navigationColor: string;
            navigationTextColor: string;
          }> &
            Partial<ThemeState>;
          const next: Partial<ThemeState> = { ...legacy };
          if (legacy.headerColor) next.headerColorLight = legacy.headerColor;
          if (legacy.navigationColor) next.navigationColorLight = legacy.navigationColor;
          if (legacy.navigationTextColor) next.navigationTextColorLight = legacy.navigationTextColor;
          next.headerColorDark ??= defaultState.headerColorDark;
          next.navigationColorDark ??= defaultState.navigationColorDark;
          next.navigationTextColorDark ??= defaultState.navigationTextColorDark;
          delete (next as Record<string, unknown>).headerColor;
          delete (next as Record<string, unknown>).navigationColor;
          delete (next as Record<string, unknown>).navigationTextColor;
          return next as ThemeState;
        }
        return persisted as ThemeState;
      },
    }
  )
);

export function useActiveChromeColors() {
  const { effectiveMode } = useTheme();
  const state = useThemeStore();
  const isDark = effectiveMode === 'dark';
  return {
    scheme: effectiveMode,
    headerColor: isDark ? state.headerColorDark : state.headerColorLight,
    navigationColor: isDark ? state.navigationColorDark : state.navigationColorLight,
    navigationTextColor: isDark ? state.navigationTextColorDark : state.navigationTextColorLight,
  };
}

interface ThemeContextValue {
  theme: Theme;
  mode: ThemeMode;
  effectiveMode: 'light' | 'dark';
  toggleMode: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { mode, setMode } = useThemeStore();

  // Determine effective mode
  const effectiveMode = mode === 'system' ? getSystemTheme() : mode;

  // Select theme based on effective mode
  const theme = effectiveMode === 'dark' ? majaDarkTheme : majaLightTheme;

  // Listen to system theme changes
  useEffect(() => {
    if (mode !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      // Force re-render when system theme changes
      setMode('system');
    };

    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, [mode, setMode]);

  const toggleMode = () => {
    const nextMode = effectiveMode === 'light' ? 'dark' : 'light';
    setMode(nextMode);
  };

  return (
    <ThemeContext.Provider value={{ theme, mode, effectiveMode, toggleMode }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
